import DeleteSweepRoundedIcon from '@mui/icons-material/DeleteSweepRounded';
import GetAppRoundedIcon from '@mui/icons-material/GetAppRounded';
import {
  Fragment,
  cloneElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Col, Row } from 'react-bootstrap';
import { notificationType } from '../../../constants/globals';
import { userActions } from '../../../constants/user-actions';
import AuthContext from '../../../context/auth_context';
import { createNotification } from '../../../utils';
import ModalDataWrapper from '../../Administration/ModalDataWrapper';
import { ActionTypes } from '../../Administration/utils';
import ApprovalButtonsHeader from '../../Reusable/Billing/ApprovalButtonsHeader';
import CustomButton from '../../Reusable/CustomButton';
import InputField from '../../Reusable/InputField';
import CustomModal from '../../Reusable/Modal/CustomModal';
import { getActionSettings, showActionButton } from '../action-utils';
import ExportDataFormContainer from '../export/ExportDataFormContainer';
import { clearFiltersForAllColumns } from '../utils';

const AgGridHeaderRow = (props) => {
  const {
      gridRef,
      translations,
      tableTitle,
      type,
      rowsChecked,
      setRefreshState,
      refreshState,
      filterActive,
      columnSettingsList,
      setAction,
      inForm,
      selectedRow,
      setSelectedRowForForm,
      useSelectedRowLabel,
      singleSelect,
      setFilterActive,
      dontShowButtons = false,
      dontShowButtonForUse = true,
      setAgGridType,
      customHeaderComponent,
    } = props || {},
    { Header, ModalForExport, gt, NotificationMessages } = translations,
    {
      quickSearchLabel,
      exportLabel,
      clearFilters,
      exportButtonTooltip,
      clearFiltersTooltip,
      useSelectedRowLabel: useSelectedRowLabelTranslation,
    } = Header;

  const [quickSearch, setQuickSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const submitRef = useRef();
  const { allowedActions } = useContext(AuthContext);

  const createActionSettings = useMemo(
    () =>
      getActionSettings(columnSettingsList, allowedActions, ActionTypes.CREATE),
    [columnSettingsList, allowedActions]
  );

  const quickSearchHandler = useCallback(
    (params) => {
      gridRef.current.api.setQuickFilter(params.target.value);
      setQuickSearch(params.target.value);
    },
    [gridRef]
  );

  useEffect(() => {
    return () => {
      setQuickSearch('');
      // gridRef.current?.api?.setQuickFilter('');
    };
  }, [type]);

  const modalCloseHandler = () => {
    setShowModal(false);
  };

  const showModalHandler = () => {
    setShowModal(true);
  };

  function clearFiltersHandler() {
    clearFiltersForAllColumns(gridRef);
    setFilterActive(false);
  }

  const actionHandler = (actionSettings) => {
    clearFiltersHandler();
    gridRef.current?.columnApi?.resetColumnState();

    if (
      actionSettings?.type !== ActionTypes.VERIFY &&
      actionSettings?.type !== ActionTypes.CONSENT_GIVE
    ) {
      gridRef.current?.api?.deselectAll();
    }
    setAction(actionSettings?.type);
  };

  const showEditTableButton = allowedActions?.includes(
    userActions.ADMINISTRATION_TABLE_UPDATE
  );

  const showCreateButton = useMemo(
    () => showActionButton(createActionSettings, allowedActions),
    [allowedActions, createActionSettings]
  );

  const onSetSelectedRowForFormHandler = async () => {
    if (!selectedRow) {
      createNotification(
        notificationType.error,
        NotificationMessages.errorTitle,
        NotificationMessages.unselectRowMessage
      );
      return;
    }
    const selectionData = singleSelect ? selectedRow : rowsChecked;

    await setSelectedRowForForm?.(selectionData);
  };

  return (
    <Fragment>
      <Row className={'pr-none'}>
        <Col>
          <Row className="d-flex align-items-start justify-content-between">
            <Col md={3} className="pr-0">
              <InputField
                id="quick-search"
                name="quick-search"
                placeholder={quickSearchLabel}
                onChange={quickSearchHandler}
                value={quickSearch}
                className="smaller-input"
                size="small"
              />
            </Col>
            <Col
              md={9}
              className="d-flex align-items-center justify-content-end py-3 pr-0 gap-2"
            >
              <CustomButton
                label={createActionSettings?.label}
                onClick={() => actionHandler(createActionSettings)}
                type="button"
                size="small"
                startIcon={
                  <i className={`fas ${createActionSettings?.icon}`} />
                }
                className="default-btn-success"
                hidden={!showCreateButton || dontShowButtons}
              />
              <CustomButton
                label={clearFilters}
                onClick={clearFiltersHandler}
                type="button"
                size="small"
                startIcon={
                  <DeleteSweepRoundedIcon fontSize="large" role="button" />
                }
                className="default-btn-danger"
                tooltipText={clearFiltersTooltip}
                disabled={!filterActive}
              />
              <CustomButton
                label={exportLabel}
                onClick={showModalHandler}
                type="button"
                size="small"
                startIcon={<GetAppRoundedIcon fontSize="large" role="button" />}
                className="default-btn-primary"
                tooltipText={exportButtonTooltip}
                disabled={rowsChecked?.length === 0}
              />
              {!inForm && !!showEditTableButton && (
                <ModalDataWrapper
                  type={type}
                  refresh={() => setRefreshState(!refreshState)}
                  tableButtonVisible
                />
              )}
            </Col>
          </Row>
          {!!inForm && setSelectedRowForForm && !setAgGridType && (
            <Row className="d-flex align-items-start justify-content-end mb-2">
              <Col className="d-flex align-items-center justify-content-end pr-0 gap-2">
                <CustomButton
                  label={useSelectedRowLabel || useSelectedRowLabelTranslation}
                  onClick={onSetSelectedRowForFormHandler}
                  type="button"
                  size="small"
                  startIcon={<i className={`fas fa-check-to-slot`} />}
                  className="default-btn-success"
                  hidden={!!dontShowButtons && !!dontShowButtonForUse} //TODO: Srediti signale
                  disabled={!selectedRow}
                />
              </Col>
            </Row>
          )}
          {customHeaderComponent && (
            <Row className="d-flex align-items-start justify-content-end mb-2">
              <Col className="m-0">
                {cloneElement(customHeaderComponent, {
                  rowsChecked,
                  selectedRow,
                  submitRef,
                  inForm,
                  translations,
                  gridRef,
                  type,
                  tableTitle,
                  gt,
                })}
              </Col>
            </Row>
          )}
          <ApprovalButtonsHeader
            actionHandler={actionHandler}
            columnSettingsList={columnSettingsList}
            type={type}
            allowedActions={allowedActions}
            rowsChecked={rowsChecked}
            dontShowButtons={dontShowButtons}
            setSelectedRowForForm={setSelectedRowForForm}
          />
        </Col>
      </Row>
      {showModal && (
        <CustomModal
          show={showModal}
          modalSize={'m'}
          hideModal={modalCloseHandler}
          title={ModalForExport.title}
          onFooterButtonClick={() => submitRef.current.click()}
          footerButtonLabel={translations.ModalForExport.buttonLabel}
          draggable={true}
          hideSaveButton={true}
          hideFooterCancelButton={true}
          submitRef={submitRef}
          exportButton={true}
          secondModal={inForm}
        >
          <ExportDataFormContainer
            translations={ModalForExport}
            gt={gt}
            gridRef={gridRef}
            submitRef={submitRef}
            tableTitle={tableTitle}
          />
        </CustomModal>
      )}
    </Fragment>
  );
};

export default AgGridHeaderRow;
